// ClientsPage.js
import React, { useState, useEffect, useRef, useLayoutEffect, useCallback } from 'react';
import { ConfigProvider, Card, Typography, Button, Table, Modal, Input, Form, Tooltip, Space, message } from 'antd';
import { SearchOutlined, EditOutlined, PlusOutlined, DeleteOutlined, SyncOutlined, LoadingOutlined } from '@ant-design/icons';
import { fetchClients, syncYetiClients, updateClient } from '../api';
import SideMenu from '../components/SideMenu';
import { renderClientsSummary } from '../components/YetiSync';

const emptyClientData = {
  client_id: '',
  client_name: '',
  contact_first_name: '',
  contact_last_name: '',
  contact_num: '',
  contact_email: '',
  secondary_email: '',
  cc_emails: [],
};

const ClientsPage = () => {
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [clientData, setClientData] = useState(emptyClientData);
  const [isFormValid, setIsFormValid] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncOpen, setSyncOpen] = useState(false);
  const [syncSummary, setSyncSummary] = useState(null);
  const [messageApi, contextHolder] = message.useMessage();

  const loadClients = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchClients();
      const sortedData = data.sort((a, b) => {
        const nameA = a.client_name || '';
        const nameB = b.client_name || '';
        return nameA.localeCompare(nameB);
      });
      setClients(sortedData);
    } catch (err) {
      console.error("Failed to load Clients:", err);
      messageApi.error("Failed to load Clients");
    } finally {
      setLoading(false);
    }
  }, [messageApi]);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  useEffect(() => {
    const filteredData = clients.filter(client => {
      const clientName = client.client_name || 'N/A';
      return clientName.toLowerCase().includes(searchQuery.toLowerCase());
    });
    setFilteredClients(filteredData);
  }, [searchQuery, clients]);

  useEffect(() => {
    const validateForm = () => {
      const { client_name, contact_first_name, contact_last_name, contact_num, contact_email, cc_emails } = clientData;
      const isEmailValid = contact_email?.includes('@') && contact_email?.includes('.');
      const formValid = client_name && contact_first_name && contact_last_name && contact_num && isEmailValid && cc_emails.every(email => email?.includes('@') && email?.includes('.'));
      setIsFormValid(formValid);
    };

    validateForm();
  }, [clientData]);

  const handleSyncClose = () => {
    setSyncSummary(null);
    setSyncOpen(false);
  };

  const handleYetiSync = async () => {
    setSyncLoading(true);
    try {
      const response = await syncYetiClients();
      setSyncSummary(response);
    } catch (error) {
      console.error("Failed to sync Clients with Yeti:", error);
      messageApi.error("Failed to sync Clients with Yeti");
    } finally {
      setSyncLoading(false);
      // The sync saves as it goes, so reload even if it failed partway
      loadClients();
    }
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleEdit = (client) => {
    if (!client) return;
    setClientData(client);
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    resetForm();
  };

  const resetForm = () => {
    setClientData(emptyClientData);
    setIsFormValid(false);
  };

  const handleChange = (field, value) => {
    setClientData(prevData => ({ ...prevData, [field]: value }));
  };

  const addCCEmailRow = () => {
    setClientData(prevData => ({
      ...prevData,
      cc_emails: [...prevData.cc_emails, '']
    }));
  };

  const handleCCEmailChange = (index, value) => {
    const updatedCCEmails = [...clientData.cc_emails];
    updatedCCEmails[index] = value;
    setClientData(prevData => ({
      ...prevData,
      cc_emails: updatedCCEmails
    }));
  };

  const deleteCCEmailRow = (index) => {
    const updatedCCEmails = [...clientData.cc_emails];
    updatedCCEmails.splice(index, 1);
    setClientData(prevData => ({
      ...prevData,
      cc_emails: updatedCCEmails
    }));
  };

  const handleSubmit = async () => {
    try {
      await updateClient(clientData, clientData.client_id);
      messageApi.success('Client updated successfully');
      closeForm();
      loadClients();
    } catch (error) {
      messageApi.error('Failed to update client');
    }
  };

  const renderCCEmailRows = () => {
    return clientData.cc_emails.map((email, index) => (
      <Form.Item key={index} style={{ marginBottom: '10px' }}>
        <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
          <Input
            placeholder="CC Email"
            value={email}
            onChange={(e) => handleCCEmailChange(index, e.target.value)}
            style={{ flexGrow: 1 }}
          />
          <DeleteOutlined
            type="danger"
            onClick={() => deleteCCEmailRow(index)}
            style={{ marginLeft: '10px', flexShrink: 0, fontSize: '18px', cursor: 'pointer' }}
          />
        </div>
      </Form.Item>
    ));
  };

  const formatPhoneNumber = (phone) => {
    const cleaned = ('' + phone).replace(/\D/g, '');
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return `(${match[1]}) ${match[2]}-${match[3]}`;
    }
    return phone;
  };

  const columns = [
    {
      title: 'Client Name',
      dataIndex: 'client_name',
      key: 'client_name',
    },
    {
      title: 'Contact Name',
      key: 'contact_name',
      render: (record) => `${record.contact_first_name} ${record.contact_last_name}`,
    },
    {
      title: 'Contact Email',
      dataIndex: 'contact_email',
      key: 'contact_email',
    },
    {
      title: 'Contact Number',
      dataIndex: 'contact_num',
      key: 'contact_num',
      render: (text) => formatPhoneNumber(text),
    },
    {
      title: 'Actions',
      key: 'actions',
      align: 'center',
      width: '10%',
      fixed: 'right',
      render: (record) => (
        <EditOutlined
          onClick={() => handleEdit(record)}
          style={{
            fontSize: '18px',
            cursor: 'pointer',
            color: 'lightgrey',
            transition: 'color 0.3s',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.color = '#f57e1f')}
          onMouseLeave={(e) => (e.currentTarget.style.color = 'lightgrey')}
        />
      ),
    },
  ];
  
  const [tableHeight, setTableHeight] = useState('100vh - 72px - 16px');
  const titleRef = useRef(null);
  const actionsRef = useRef(null);
  const tableRef = useRef(null);

  useLayoutEffect(() => {
    const calculateTableHeight = () => {
      const headerHeight = 72;
      const titleHeight = titleRef.current ? titleRef.current.offsetHeight : 48;
      const actionsHeight = actionsRef.current ? actionsRef.current.offsetHeight : 32;
      const padding = 16;

      // Calculate height of the table header
      const tableHeaderHeight = tableRef.current ? tableRef.current.querySelector('.ant-table-thead').offsetHeight : 55;

      const totalHeightAroundTableRows =
        headerHeight +
        titleHeight +
        actionsHeight +
        padding * 4 +
        tableHeaderHeight;
      console.log(totalHeightAroundTableRows);
      setTableHeight(`calc(100vh - ${totalHeightAroundTableRows}px)`);
    };

    calculateTableHeight();

    window.addEventListener('resize', calculateTableHeight);
    return () => window.removeEventListener('resize', calculateTableHeight);
  }, []);

  return (
    <ConfigProvider theme={{ token: { colorPrimary: '#F57D20' } }}>
      {contextHolder}
      <div style={{ display: 'flex' }}>
        <SideMenu />

        <Card style={{ height: 'calc(100vh - 72px - 16px)', marginRight: '16px', overflow: 'hidden', boxShadow: '0 0 10px rgba(0, 0, 0, 0.1)' }}>
          <Typography.Title ref={titleRef} level={2} style={{ margin: 0, paddingBottom: '10px' }}>
            Clients
          </Typography.Title>

          <div ref={actionsRef} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <Input
              placeholder="Search Clients"
              value={searchQuery}
              onChange={handleSearchChange}
              style={{ width: '300px' }}
              prefix={<SearchOutlined style={{ color: '#d9d9d9' }} />}
            />
            <Tooltip title="Sync Clients with Yeti">
              <Button
                shape="circle"
                icon={<SyncOutlined />}
                onClick={() => setSyncOpen(true)}
                disabled={syncLoading}
              />
            </Tooltip>
          </div>

          <div ref={tableRef} style={{ overflow: 'hidden', marginTop: '16px' }}>
            <Table
              columns={columns}
              dataSource={filteredClients}
              pagination={false}
              scroll={{ y: tableHeight }}
              sticky
              loading={loading}
            />
          </div>
        </Card>
      </div>

      {/* Edit Client Modal */}
      <Modal
        open={formOpen}
        onCancel={closeForm}
        title="Edit Client"
        onOk={handleSubmit}
        centered
        okButtonProps={{ disabled: !isFormValid }}
      >
        <Form layout="vertical">
          <Form.Item label="Client Name">
            <Input
              placeholder="Client Name"
              value={clientData.client_name}
              onChange={(e) => handleChange('client_name', e.target.value)}
            />
          </Form.Item>

          <Form.Item label="Contact First Name">
            <Input
              placeholder="Contact First Name"
              value={clientData.contact_first_name}
              onChange={(e) => handleChange('contact_first_name', e.target.value)}
            />
          </Form.Item>

          <Form.Item label="Contact Last Name">
            <Input
              placeholder="Contact Last Name"
              value={clientData.contact_last_name}
              onChange={(e) => handleChange('contact_last_name', e.target.value)}
            />
          </Form.Item>

          <Form.Item label="Contact Number">
            <Input
              placeholder="Contact Number"
              value={clientData.contact_num}
              onChange={(e) => handleChange('contact_num', e.target.value)}
            />
          </Form.Item>

          <Form.Item label="Contact Email">
            <Input
              placeholder="Contact Email"
              value={clientData.contact_email}
              onChange={(e) => handleChange('contact_email', e.target.value)}
            />
          </Form.Item>

          <Form.Item label="CC Emails">
            {renderCCEmailRows()}
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={addCCEmailRow}
              style={{ marginTop: '10px' }}
            >
              Add CC Email
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={syncSummary ? "Yeti Client Sync Summary" : "Sync Clients with Yeti"}
        open={syncOpen}
        onCancel={handleSyncClose}
        footer={[
          <Button key="close" onClick={handleSyncClose} disabled={syncLoading}>Close</Button>,
          !syncSummary && <Button key="start" type="primary" onClick={handleYetiSync} disabled={syncLoading}>Start Client Sync</Button>
        ]}
      >
        {syncLoading ? (
          <Space direction="horizontal" size="middle">
            <Typography variant='text'>Syncing Clients</Typography>
            <LoadingOutlined style={{ fontSize: '32px' }} spin />
          </Space>
        ) : syncSummary ? (
          <div>
            <br/>
            {renderClientsSummary(syncSummary)}
          </div>
        ) : (
          <Typography variant='text'>Syncing Clients with Yeti will add new Clients and update existing Client records.</Typography>
        )}
      </Modal>
    </ConfigProvider>
  );
};

export default ClientsPage;